import React from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu, Dropdown } from 'antd';
import { ClickParam } from 'antd/lib/menu';
import { ColumnProps, PaginationConfig, SortOrder } from 'antd/lib/table';
import UserContext from 'src/services/UserContext';
import { objectKeys } from 'src/utils/objectKeys';
import { Sorting } from 'src/types/Sorting';
import UserInfo from 'src/types/UserInfo';
import { arrayWrap } from 'src/utils/arrayWrap';
import ProductSegment from '../../types/ProductSegment';
import { OperationType } from '../../types/SelectedOperation';
import TableColumnsFilter from 'src/components/common/TableColumnsFilter';
import TextWithEllipsis from 'src/components/common/TextWithEllipsis';
import styles from './ProductSegmentTable.module.less';

export const defaultPagination: PaginationConfig = {
  hideOnSinglePage: false,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50'],
  locale: { items_per_page: '' },
};

export type ColumnId = keyof ProductSegment;

export type ColumnSorting = Sorting<ProductSegment> & {
  field: ColumnId;
};

type StandardColumn = ColumnProps<ProductSegment>;

type FilterableColumn = StandardColumn & { dataIndex: ColumnId };

export type ProductSegmentColumn = FilterableColumn | StandardColumn;

export type ColumnsFilterOption = {
  id: ColumnId;
  name: string;
  disabled?: boolean;
};

export const defaultColumnIds: ColumnId[] = [
  'active',
  'userName',
  'productSegmentName',
  'productSegmentDescription',
  'favourite',
  'shared',
  'insertTimestamp',
];

const MAX_ROW_LENGTH = 55;

export const columnsFilterOptions: ColumnsFilterOption[] = [
  {
    id: 'favourite',
    name: 'Favorite',
    disabled: true,
  },
  {
    id: 'productSegmentName',
    name: 'Segment Name',
    disabled: true,
  },
  {
    id: 'productSegmentDescription',
    name: 'Segment Description',
  },
  {
    id: 'userName',
    name: 'Created By',
  },
  {
    id: 'insertTimestamp',
    name: 'Created At',
  },
  {
    id: 'updateTimestamp',
    name: 'Updated At',
  },
  {
    id: 'active',
    name: 'Segment Status',
    disabled: true,
  },
  {
    id: 'shared',
    name: 'Shared',
  },
];

const renderStarIcon = (value: boolean, onClick: () => void, readOnlyMode?: boolean) => {
  return (
    <div>
      <FontAwesomeIcon
        className={clsx([value ? styles.starIconSolid : styles.starIcon, readOnlyMode && styles.cursorNotAllowed])}
        icon={[value ? 'fas' : 'fal', 'star']}
        size="lg"
        onClick={onClick}
      />
    </div>
  );
};

const getBooleanRender = (
  render: (v: boolean, fn: () => void, readOnlyMode?: boolean) => React.ReactElement,
  onChange: (v: boolean, r: ProductSegment) => void,
  readOnlyMode?: boolean
) => {
  return (value: boolean, record: ProductSegment) => render(value, () => onChange(value, record), readOnlyMode);
};

const renderSegmentName = (val: string, onClick: () => void) => {
  return (
    <a onClick={onClick} title="Edit Product Segment">
      {val}
    </a>
  );
};

const renderDescription = (val: string | null) => {
  return <TextWithEllipsis value={val} maxLength={MAX_ROW_LENGTH} />;
};

const renderTimestamp = (timestamp: string | null) => {
  const dateFormat = UserContext.getDateFormat();
  return UserContext.reformatUTCDate(timestamp, `${dateFormat} HH:mm`);
};

const renderCreated = (timestamp: string | null, record: ProductSegment) => {
  if (timestamp) {
    return (
      <>
        {renderTimestamp(timestamp)}
        <br />
        {record.userName}
      </>
    );
  }
  return record.userName;
};

const renderSegmentStatus = (status: boolean) => {
  return status ? 'Active' : 'Inactive';
};

const renderShared = (shared: boolean) => {
  return shared ? 'Public' : 'Private';
};

const renderActionsTitle = (onColumnsFilterChange: (value: string) => void, readOnlyMode?: boolean) => {
  return (
    <>
      {!readOnlyMode && 'Actions '}
      <TableColumnsFilter
        onChange={onColumnsFilterChange}
        defaultColumns={defaultColumnIds}
        columnOptions={columnsFilterOptions}
      />
    </>
  );
};

const renderActionsMenu = (isSegmentOwner: boolean, onClick: (e: ClickParam) => void) => {
  const menuItems = {
    [OperationType.AlertManagement]: 'Alert Management',
    [OperationType.ConfigureAssociatedProducts]: 'Product Association',
    [OperationType.CloneSegment]: 'Clone Segment',
    [OperationType.DeleteSegment]: 'Delete Segment',
  };
  return (
    <Menu className={styles.actionsMenu} onClick={onClick}>
      {objectKeys(menuItems).map((key) => {
        if (key !== OperationType.DeleteSegment || isSegmentOwner) {
          return <Menu.Item key={key}>{menuItems[key]}</Menu.Item>;
        }
      })}
    </Menu>
  );
};

const renderActions = (
  user: UserInfo | null,
  record: ProductSegment,
  onAction: (record: ProductSegment, type: OperationType) => void,
  readOnlyMode?: boolean
) => {
  if (readOnlyMode) {
    return null;
  }

  const isSegmentOwner = user?.userName === record.userName;
  // TODO remove type assertion since it's better to avoid ...
  const handleMenuClick = (e: ClickParam) => {
    onAction(record, e.key as OperationType);
  };

  return (
    <Dropdown overlay={renderActionsMenu(isSegmentOwner, handleMenuClick)}>
      <FontAwesomeIcon icon={['fal', 'ellipsis-h']} className={styles.actionMoreIcon} size={'3x'} />
    </Dropdown>
  );
};

const getColumnCreated = (selectedIds: ColumnId[]): FilterableColumn | null => {
  const isUserNameSelected = selectedIds.includes('userName');
  const isTimestampSelected = selectedIds.includes('insertTimestamp');

  if (isUserNameSelected && isTimestampSelected) {
    return {
      title: 'Created',
      dataIndex: 'insertTimestamp',
      render: renderCreated,
      sorter: true,
    };
  }

  if (isUserNameSelected) {
    return {
      title: 'Created By',
      dataIndex: 'userName',
      sorter: true,
    };
  }

  if (isTimestampSelected) {
    return {
      title: 'Created At',
      dataIndex: 'insertTimestamp',
      render: renderTimestamp,
      sorter: true,
    };
  }

  return null;
};

const getFilterableColumns = (
  selectedIds: ColumnId[],
  onFavoriteChange: (value: boolean, record: ProductSegment) => void,
  onAction: (record: ProductSegment, type: OperationType) => void,
  readOnlyMode?: boolean
): FilterableColumn[] => {
  const columns: FilterableColumn[] = [
    {
      width: 80,
      title: 'Favorite',
      dataIndex: 'favourite',
      render: getBooleanRender(renderStarIcon, onFavoriteChange, readOnlyMode),
      sorter: true,
    },
    {
      title: 'Client ID',
      dataIndex: 'clientId',
      sorter: true,
    },
    {
      title: 'Segment ID',
      dataIndex: 'productSegmentId',
      sorter: true,
    },
    {
      title: 'Segment Name',
      dataIndex: 'productSegmentName',
      render: (_, record: ProductSegment) =>
        renderSegmentName(record.productSegmentName, () => onAction(record, OperationType.EditSegment)),
      sorter: true,
    },
    {
      title: 'Segment Description',
      dataIndex: 'productSegmentDescription',
      render: renderDescription,
      sorter: true,
    },
    {
      title: 'Updated At',
      dataIndex: 'updateTimestamp',
      render: renderTimestamp,
      sorter: true,
    },
    {
      title: 'Segment Status',
      dataIndex: 'active',
      render: renderSegmentStatus,
      sorter: true,
    },
    {
      title: 'Shared',
      dataIndex: 'shared',
      render: renderShared,
      sorter: true,
    },
    ...arrayWrap(getColumnCreated(selectedIds)),
  ];

  return columns;
};

export const getColumns = (
  user: UserInfo | null,
  sorting: ColumnSorting,
  selectedColumnIds: ColumnId[],
  onColumnsFilterChange: (value: string) => void,
  onFavoriteChange: (value: boolean, record: ProductSegment) => void,
  onAction: (record: ProductSegment, type: OperationType) => void
): ProductSegmentColumn[] => {
  const readOnlyMode = user?.appPermissions['read_only_product_alert'];

  const sortOrder = (key: ColumnId): SortOrder | false => {
    return sorting.field === key ? sorting.order : false;
  };

  const columns = getFilterableColumns(selectedColumnIds, onFavoriteChange, onAction, readOnlyMode);

  const filteredColumns = columns
    .filter((column) => selectedColumnIds.includes(column.dataIndex))
    .map((column) => ({
      ...column,
      ...(column.sorter && { sortOrder: sortOrder(column.dataIndex) }),
    }));

  return [
    ...filteredColumns,
    {
      title: renderActionsTitle(onColumnsFilterChange, readOnlyMode),
      key: 'actions',
      fixed: 'right',
      render: (_, record: ProductSegment) => renderActions(user, record, onAction, readOnlyMode),
    },
  ];
};
